import React from 'react';
import { PredictionModel, Symbol, BacktestSettingsType, RLDQNSettings, HDKSettings } from '../types';
import { 
  Slider, 
} from '@/components/ui/slider';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';

export interface ModelParametersProps {
  model: PredictionModel;
  onModelChange: (changes: any) => void;
  symbols: Symbol[];
  backtestSettings: BacktestSettingsType;
}

export const ModelParameters: React.FC<ModelParametersProps> = ({
  model,
  onModelChange,
  symbols,
  backtestSettings
}) => {
  const updateStewieQuantSettings = (changes: Partial<any>) => {
    const current = model.stewieQuantSettings || {
      optimizerLevel: 3,
      adaptiveThreshold: 0.75,
      correlationThreshold: 0.7,
      zscoreThreshold: 2.0,
      reversionHalfLife: 15
    };
    onModelChange({ stewieQuantSettings: { ...current, ...changes } });
  };

  const updateLSTMSettings = (changes: Partial<any>) => {
    const current = model.lstmSettings || {
      lookbackWindow: 30,
      predictionHorizon: 5,
      numLayers: 2,
      numUnits: 64,
      dropoutRate: 0.2,
      epochs: 100,
      batchSize: 32,
      learningRate: 0.001,
      optimizer: 'adam'
    };
    onModelChange({ lstmSettings: { ...current, ...changes } });
  };

  const updateARIMASettings = (changes: Partial<any>) => {
    const current = model.arimaSettings || {
      p: 1,
      d: 1,
      q: 1,
      seasonal: false,
      P: 1,
      D: 1,
      Q: 1,
      m: 12,
      forecastHorizon: 5,
      confidenceLevel: 0.95
    };
    onModelChange({ arimaSettings: { ...current, ...changes } });
  };

  const updateXGBoostSettings = (changes: Partial<any>) => {
    const current = model.xgboostSettings || {
      predictMode: 'classification',
      featureWindow: 10,
      predictionHorizon: 1,
      n_estimators: 100,
      learning_rate: 0.1,
      max_depth: 3,
      subsample: 0.8,
      colsample_bytree: 0.8,
      gamma: 0,
      objective: 'binary:logistic'
    };
    onModelChange({ xgboostSettings: { ...current, ...changes } });
  };

  const updateFinGPTSettings = (changes: Partial<any>) => {
    const current = model.stewieGPTSettings || {
      promptTemplate: "Analyze the following market data and news for {symbol}:\n{data}\n{news}\nProvide buy/sell signal with confidence score.",
      maxOutputTokens: 500,
      temperature: 0.3,
      confidenceThreshold: 0.7,
      volatilityThreshold: 0.1,
      newsSourceFilter: ['Bloomberg', 'Reuters', 'CNBC']
    };
    onModelChange({ stewieGPTSettings: { ...current, ...changes } });
  };

  const updateMonteCarloSettings = (changes: Partial<any>) => {
    const current = model.monteCarloSettings || {
      simulationCount: 1000,
      timeHorizon: 30,
      distribution: 'normal',
      includeTailRisks: true,
      confidenceInterval: 0.95,
      volShockEnabled: false,
      volShockMagnitude: 2.0
    };
    onModelChange({ monteCarloSettings: { ...current, ...changes } });
  };

  const updateDijkstraSettings = (changes: Partial<any>) => {
    const current = model.dijkstraSettings || {
      pathOptimization: 'combined',
      riskWeight: 0.5,
      returnWeight: 0.5,
      includeFees: true,
      maxIterations: 100
    };
    onModelChange({ dijkstraSettings: { ...current, ...changes } });
  };

  const updateRLDQNSettings = (changes: Partial<RLDQNSettings>) => {
    const current = model.rlDqnSettings || {
      timesteps: 10000,
      learning_rate: 0.001,
      gamma: 0.99,
      risk_aversion_param: 0.1,
      generate_signal_with_conviction: false,
      buy_conviction_threshold: 0.1,
      sell_conviction_threshold: 0.1,
      exploration_rate: 0.1,
      memory_size: 10000,
      batch_size: 32
    };
    onModelChange({ rlDqnSettings: { ...current, ...changes } });
  };

  const updateHDKSettings = (changes: Partial<HDKSettings>) => {
    const current = model.hdkSettings || {
      kernelType: 'rbf',
      dimensions: 5,
      learningRate: 0.01,
      regularization: 0.001,
      window: 20,
      gamma: 0.1,
      polyDegree: 3,
      coef0: 0,
      useAdaptiveKernel: true,
      featureNormalization: 'z-score',
      zscoreThreshold: 2.0
    };
    onModelChange({ hdkSettings: { ...current, ...changes } });
  };

  const renderStewieQuantParameters = () => {
    const settings = model.stewieQuantSettings || {
      optimizerLevel: 3,
      adaptiveThreshold: 0.75,
      correlationThreshold: 0.7,
      zscoreThreshold: 2.0,
      reversionHalfLife: 15
    };

    // Helper function to get optimizer level description
    const getOptimizerLevelDescription = (level: number) => {
      const descriptions = {
        1: { name: "Simple Trends", emoji: "📈", description: "Basic trend following with moving averages", bestFor: "New to algorithmic trading", risk: "🟢 Conservative" },
        2: { name: "Momentum Awareness", emoji: "🎯", description: "Adds RSI to catch overbought/oversold opportunities", bestFor: "Want to avoid buying peaks", risk: "🟡 Moderate" },
        3: { name: "Statistical Edge", emoji: "🔬", description: "Uses Z-score and Bollinger Bands for mean reversion", bestFor: "Comfortable with data-driven approaches", risk: "🟡 Moderate-Advanced" },
        4: { name: "Multi-Factor Analysis", emoji: "⚡", description: "Combines trend and momentum with MACD confirmation", bestFor: "Want institutional-grade signals", risk: "🔴 Advanced" },
        5: { name: "Market Regime Adaptive", emoji: "🌊", description: "Adapts to market conditions automatically", bestFor: "Want sophisticated risk management", risk: "🔴 Expert" }
      };
      return descriptions[level as keyof typeof descriptions] || descriptions[3];
    };

    // Helper function to get adaptive threshold description
    const getAdaptiveThresholdDescription = (threshold: number) => {
      if (threshold <= 0.3) return { name: "Hair Trigger", description: "Many signals, catches small moves but may have false alarms", style: "Active day trading" };
      if (threshold <= 0.6) return { name: "Balanced", description: "Moderate signal frequency with good balance", style: "Swing trading" };
      if (threshold <= 0.9) return { name: "Selective", description: "Fewer but higher-confidence signals", style: "Position trading" };
      return { name: "Ultra Conservative", description: "Only trades when extremely confident", style: "Long-term investing" };
    };

    // Helper function to get correlation threshold description
    const getCorrelationDescription = (threshold: number) => {
      if (threshold <= 0.4) return { name: "Loose Relationships", description: "Accepts signals when indicators don't strongly agree", analogy: "Taking advice when only some friends agree" };
      if (threshold <= 0.7) return { name: "Moderate Agreement", description: "Requires decent agreement between analysis methods", analogy: "Making decisions when most friends agree" };
      return { name: "Strong Consensus", description: "Only trades when all methods strongly align", analogy: "Only acting when everyone unanimously agrees" };
    };

    // Helper function to get Z-score description
    const getZScoreDescription = (threshold: number) => {
      if (threshold <= 1.5) return { name: "Frequent Opportunities", description: "Trades on moderately unusual price movements", frequency: "~16% of market days", bestFor: "Active trading in normal markets" };
      if (threshold <= 2.5) return { name: "Significant Moves", description: "Waits for statistically meaningful deviations", frequency: "~5% of market days", bestFor: "Quality over quantity approach" };
      return { name: "Rare Events", description: "Only trades on highly unusual movements", frequency: "<1% of market days", bestFor: "Crisis/opportunity trading" };
    };

    // Helper function to get reversion half-life description
    const getReversionDescription = (days: number) => {
      if (days <= 10) return { name: "Short-Term Focus", description: "Assumes quick reversals like weekly news cycles", marketStyle: "Reacts to earnings, news events", bestFor: "Fast-moving, volatile stocks" };
      if (days <= 20) return { name: "Medium-Term View", description: "Balanced approach for monthly business cycles", marketStyle: "Captures quarterly earnings cycles", bestFor: "Most stock types, balanced approach" };
      return { name: "Long-Term Perspective", description: "Assumes fundamental changes take time", marketStyle: "Captures seasonal patterns", bestFor: "Large-cap, slower-moving stocks" };
    };

    const optimizerDesc = getOptimizerLevelDescription(settings.optimizerLevel);
    const adaptiveDesc = getAdaptiveThresholdDescription(settings.adaptiveThreshold);
    const correlationDesc = getCorrelationDescription(settings.correlationThreshold);
    const zscoreDesc = getZScoreDescription(settings.zscoreThreshold);
    const reversionDesc = getReversionDescription(settings.reversionHalfLife);

    return (
      <TooltipProvider>
        <div className="space-y-6 mt-4">
          {/* Optimizer Level - Trading Intelligence Level */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-sm font-medium">Trading Intelligence Level</Label>
              <span className="text-sm font-mono bg-blue-100 px-2 py-1 rounded">
                Level {settings.optimizerLevel}
              </span>
            </div>
            
            {/* Level Description Card */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{optimizerDesc.emoji}</span>
                <span className="font-semibold text-blue-900">{optimizerDesc.name}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{optimizerDesc.risk}</span>
              </div>
              <p className="text-sm text-blue-800 mb-1">{optimizerDesc.description}</p>
              <p className="text-xs text-blue-600">Best for: {optimizerDesc.bestFor}</p>
            </div>
            
            {/* Step Progress Bar */}
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Basic</span>
                <span>Moderate</span>
                <span>Advanced</span>
                <span>Expert</span>
              </div>
              <Slider
                value={[settings.optimizerLevel]}
                min={1}
                max={5}
                step={1}
                onValueChange={(value) => updateStewieQuantSettings({ optimizerLevel: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1</span>
                <span>2</span>
                <span>3</span>
                <span>4</span>
                <span>5</span>
              </div>
            </div>
          </div>

          {/* Signal Sensitivity Dial */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Signal Sensitivity</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">Controls how sensitive the algorithm is to trading opportunities. Lower values generate more signals.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-green-100 px-2 py-1 rounded">
                {settings.adaptiveThreshold.toFixed(2)}
              </span>
            </div>
            
            {/* Sensitivity Description */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-green-900">{adaptiveDesc.name}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{adaptiveDesc.style}</span>
              </div>
              <p className="text-sm text-green-800">{adaptiveDesc.description}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>🔇 Sensitive</span>
                <span>Conservative 🔊</span>
              </div>
              <Slider
                value={[settings.adaptiveThreshold]}
                min={0.1}
                max={1.0}
                step={0.05}
                onValueChange={(value) => updateStewieQuantSettings({ adaptiveThreshold: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0.1</span>
                <span>0.5</span>
                <span>1.0</span>
              </div>
            </div>
          </div>

          {/* Relationship Strength Filter */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Indicator Agreement</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How much the different technical indicators need to agree before generating a signal.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-purple-100 px-2 py-1 rounded">
                {settings.correlationThreshold.toFixed(2)}
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-purple-900">{correlationDesc.name}</span>
              </div>
              <p className="text-sm text-purple-800 mb-1">{correlationDesc.description}</p>
              <p className="text-xs text-purple-600 italic">Like {correlationDesc.analogy}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>👥 Loose</span>
                <span>Strong 👫</span>
              </div>
              <Slider
                value={[settings.correlationThreshold]}
                min={0.1}
                max={0.95}
                step={0.05}
                onValueChange={(value) => updateStewieQuantSettings({ correlationThreshold: value[0] })}
                className="w-full"
              />
            </div>
          </div>

          {/* Statistical Significance Meter */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Statistical Significance</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How unusual a price movement needs to be before the algorithm considers it a trading opportunity. Higher values mean rarer but potentially more significant signals.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-orange-100 px-2 py-1 rounded">
                {settings.zscoreThreshold.toFixed(1)}σ
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-orange-900">{zscoreDesc.name}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{zscoreDesc.frequency}</span>
              </div>
              <p className="text-sm text-orange-800 mb-1">{zscoreDesc.description}</p>
              <p className="text-xs text-orange-600">Best for: {zscoreDesc.bestFor}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>📊 Normal</span>
                <span>Extreme 📈</span>
              </div>
              <Slider
                value={[settings.zscoreThreshold]}
                min={1.0}
                max={3.5}
                step={0.1}
                onValueChange={(value) => updateStewieQuantSettings({ zscoreThreshold: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1.0 (84%)</span>
                <span>2.0 (5%)</span>
                <span>3.0 (0.3%)</span>
              </div>
            </div>
          </div>

          {/* Memory Length */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Memory Length</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How long the algorithm "remembers" price movements when calculating mean reversion. Shorter periods react faster to recent changes.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-teal-100 px-2 py-1 rounded">
                {settings.reversionHalfLife} days
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-teal-900">{reversionDesc.name}</span>
              </div>
              <p className="text-sm text-teal-800 mb-1">{reversionDesc.description}</p>
              <p className="text-xs text-teal-600">
                <span className="font-medium">Market Style:</span> {reversionDesc.marketStyle}
              </p>
              <p className="text-xs text-teal-600">
                <span className="font-medium">Best for:</span> {reversionDesc.bestFor}
              </p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>📅 Short Memory</span>
                <span>Long Memory 🧠</span>
              </div>
              <Slider
                value={[settings.reversionHalfLife]}
                min={5}
                max={40}
                step={1}
                onValueChange={(value) => updateStewieQuantSettings({ reversionHalfLife: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>5</span>
                <span>15</span>
                <span>40</span>
              </div>
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  };

  const renderLSTMParameters = () => {
    const settings = model.lstmSettings || {
      lookbackWindow: 30,
      predictionHorizon: 5,
      numLayers: 2,
      numUnits: 64,
      dropoutRate: 0.2,
      epochs: 100,
      batchSize: 32,
      learningRate: 0.001,
      optimizer: 'adam'
    };

    // Helper functions for user-friendly descriptions
    const getLookbackDescription = (window: number) => {
      if (window <= 10) return { name: "Short Memory", description: "Focuses on recent price patterns", emoji: "⚡", timeframe: "1-2 weeks", bestFor: "Day trading signals" };
      if (window <= 30) return { name: "Medium Memory", description: "Balances recent and historical patterns", emoji: "🔄", timeframe: "1 month", bestFor: "Swing trading" };
      if (window <= 60) return { name: "Long Memory", description: "Considers extended price history", emoji: "📚", timeframe: "2-3 months", bestFor: "Position trading" };
      return { name: "Deep Memory", description: "Analyzes long-term market cycles", emoji: "🧠", timeframe: "3+ months", bestFor: "Trend following" };
    };

    const getNetworkComplexityDescription = (layers: number, units: number) => {
      const totalParams = layers * units * units; // Simplified calculation
      if (totalParams <= 4000) return { 
        level: "Simple Network", 
        power: "🟢 Fast & Stable", 
        description: "Quick training, good for clean patterns", 
        training: "Fast",
        risk: "Low overfitting"
      };
      if (totalParams <= 16000) return { 
        level: "Moderate Network", 
        power: "🟡 Balanced", 
        description: "Good accuracy with reasonable complexity", 
        training: "Medium",
        risk: "Moderate overfitting"
      };
      return { 
        level: "Complex Network", 
        power: "🔴 High Power", 
        description: "Can find subtle patterns but may overfit", 
        training: "Slow",
        risk: "High overfitting"
      };
    };

    const getDropoutDescription = (rate: number) => {
      if (rate <= 0.1) return { name: "Minimal Protection", description: "Slight overfitting prevention", emoji: "🛡️", level: "Light", effect: "Preserves most learning" };
      if (rate <= 0.3) return { name: "Balanced Protection", description: "Good balance of learning and stability", emoji: "⚖️", level: "Medium", effect: "Prevents most overfitting" };
      if (rate <= 0.5) return { name: "Strong Protection", description: "Heavy overfitting prevention", emoji: "🔒", level: "Heavy", effect: "Very conservative learning" };
      return { name: "Extreme Protection", description: "Maximum stability but slower learning", emoji: "🛡️", level: "Maximum", effect: "May hurt performance" };
    };

    const getTrainingDescription = (epochs: number, batchSize: number) => {
      const totalIterations = epochs * (1000 / batchSize); // Assuming ~1000 data points
      if (totalIterations <= 5000) return { 
        type: "Quick Training", 
        duration: "⚡ Fast", 
        description: "Basic pattern learning", 
        quality: "Good for simple patterns",
        time: "Minutes"
      };
      if (totalIterations <= 20000) return { 
        type: "Standard Training", 
        duration: "🔄 Medium", 
        description: "Thorough pattern recognition", 
        quality: "Good overall performance",
        time: "~1 hour"
      };
      return { 
        type: "Deep Training", 
        duration: "🐌 Slow", 
        description: "Comprehensive pattern mastery", 
        quality: "High accuracy potential",
        time: "Hours"
      };
    };

    const lookbackDesc = getLookbackDescription(settings.lookbackWindow);
    const complexityDesc = getNetworkComplexityDescription(settings.numLayers, settings.numUnits);
    const dropoutDesc = getDropoutDescription(settings.dropoutRate);
    const trainingDesc = getTrainingDescription(settings.epochs, settings.batchSize);

    return (
      <TooltipProvider>
        <div className="space-y-6 mt-4">
          {/* Network Overview */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-blue-900">LSTM Neural Network</h3>
              <span className="text-sm font-mono bg-blue-200 px-3 py-1 rounded-full">
                {complexityDesc.level}
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="font-medium">{complexityDesc.power}</span>
              <span className="text-blue-700">{complexityDesc.description}</span>
              <span className="text-xs bg-white px-2 py-1 rounded">⚡ {complexityDesc.training}</span>
            </div>
          </div>

          {/* Memory Scope - Lookback Window */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Memory Scope (Pattern History)</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How many days of price history the neural network analyzes to identify patterns.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-green-100 px-2 py-1 rounded">
                {settings.lookbackWindow} days
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{lookbackDesc.emoji}</span>
                <span className="font-semibold text-green-900">{lookbackDesc.name}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{lookbackDesc.timeframe}</span>
              </div>
              <p className="text-sm text-green-800 mb-1">{lookbackDesc.description}</p>
              <p className="text-xs text-green-600">Best for: {lookbackDesc.bestFor}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>⚡ Short-term</span>
                <span>Long-term 🧠</span>
              </div>
              <Slider
                value={[settings.lookbackWindow]}
                min={5}
                max={100}
                step={5}
                onValueChange={(value) => updateLSTMSettings({ lookbackWindow: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>5</span>
                <span>30</span>
                <span>60</span>
                <span>100</span>
              </div>
            </div>
          </div>

          {/* Prediction Horizon */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Prediction Horizon</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How many days into the future the model predicts. Shorter horizons are more accurate.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-purple-100 px-2 py-1 rounded">
                {settings.predictionHorizon} day{settings.predictionHorizon > 1 ? 's' : ''}
              </span>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>🎯 Tomorrow</span>
                <span>Long-term 📅</span>
              </div>
              <Slider
                value={[settings.predictionHorizon]}
                min={1}
                max={30}
                step={1}
                onValueChange={(value) => updateLSTMSettings({ predictionHorizon: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1</span>
                <span>5</span>
                <span>15</span>
                <span>30</span>
              </div>
            </div>
          </div>

          {/* Network Architecture */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Network Architecture</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">The structure of the neural network. More layers and units can learn complex patterns but may overfit.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-indigo-100 px-2 py-1 rounded">
                {settings.numLayers}×{settings.numUnits}
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-indigo-900">{complexityDesc.level}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{complexityDesc.risk}</span>
              </div>
              <p className="text-sm text-indigo-800 mb-1">{complexityDesc.description}</p>
              <p className="text-xs text-indigo-600">Training: {complexityDesc.training}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-gray-600 mb-1 block">Network Depth (Layers)</Label>
                <Slider
                  value={[settings.numLayers]}
                  min={1}
                  max={5}
                  step={1}
                  onValueChange={(value) => updateLSTMSettings({ numLayers: value[0] })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1</span>
                  <span>3</span>
                  <span>5</span>
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-gray-600 mb-1 block">Layer Width (Units)</Label>
                <Slider
                  value={[settings.numUnits]}
                  min={16}
                  max={256}
                  step={16}
                  onValueChange={(value) => updateLSTMSettings({ numUnits: value[0] })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>16</span>
                  <span>64</span>
                  <span>256</span>
                </div>
              </div>
            </div>
          </div>

          {/* Overfitting Protection */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Overfitting Protection (Dropout)</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">Randomly "drops out" neurons during training to prevent overfitting. Higher rates = more protection but slower learning.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-orange-100 px-2 py-1 rounded">
                {(settings.dropoutRate * 100).toFixed(0)}%
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{dropoutDesc.emoji}</span>
                <span className="font-semibold text-orange-900">{dropoutDesc.name}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{dropoutDesc.level}</span>
              </div>
              <p className="text-sm text-orange-800 mb-1">{dropoutDesc.description}</p>
              <p className="text-xs text-orange-600">{dropoutDesc.effect}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>🛡️ Light protection</span>
                <span>Heavy protection 🔒</span>
              </div>
              <Slider
                value={[settings.dropoutRate]}
                min={0.0}
                max={0.8}
                step={0.05}
                onValueChange={(value) => updateLSTMSettings({ dropoutRate: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0%</span>
                <span>20%</span>
                <span>50%</span>
                <span>80%</span>
              </div>
            </div>
          </div>

          {/* Training Configuration */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-sm font-medium">Training Configuration</Label>
              <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                {trainingDesc.type}
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-gray-900">{trainingDesc.type}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{trainingDesc.duration}</span>
              </div>
              <p className="text-sm text-gray-800 mb-1">{trainingDesc.description}</p>
              <div className="flex justify-between text-xs text-gray-600">
                <span>{trainingDesc.quality}</span>
                <span>Est. time: {trainingDesc.time}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs">Training Cycles (Epochs)</Label>
                  <span className="text-xs font-mono">{settings.epochs}</span>
                </div>
                <Slider
                  value={[settings.epochs]}
                  min={10}
                  max={500}
                  step={10}
                  onValueChange={(value) => updateLSTMSettings({ epochs: value[0] })}
                />
                <div className="flex justify-between text-xs text-gray-400">
                  <span>10</span>
                  <span>100</span>
                  <span>500</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs">Batch Size</Label>
                  <span className="text-xs font-mono">{settings.batchSize}</span>
                </div>
                <Slider
                  value={[settings.batchSize]}
                  min={8}
                  max={128}
                  step={8}
                  onValueChange={(value) => updateLSTMSettings({ batchSize: value[0] })}
                />
                <div className="flex justify-between text-xs text-gray-400">
                  <span>8</span>
                  <span>32</span>
                  <span>128</span>
                </div>
              </div>
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Advanced Settings</Label>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs">Learning Rate</Label>
                  <span className="text-xs font-mono">{settings.learningRate || 0.001}</span>
                </div>
                <Slider
                  value={[settings.learningRate || 0.001]}
                  min={0.0001}
                  max={0.01}
                  step={0.0001}
                  onValueChange={(value) => updateLSTMSettings({ learningRate: value[0] })}
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs">Optimizer</Label>
                <Select
                  value={settings.optimizer || 'adam'}
                  onValueChange={(value) => updateLSTMSettings({ optimizer: value })}
                >
                  <SelectTrigger className="text-xs h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="adam">Adam (Recommended)</SelectItem>
                    <SelectItem value="rmsprop">RMSprop</SelectItem>
                    <SelectItem value="sgd">SGD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  };

  const renderARIMAParameters = () => {
    const settings = model.arimaSettings || {
      p: 1,
      d: 1,
      q: 1,
      seasonal: false,
      P: 1,
      D: 1,
      Q: 1,
      m: 12,
      forecastHorizon: 5,
      confidenceLevel: 0.95
    };

    // Helper functions for user-friendly descriptions
    const getAROrderDescription = (p: number) => {
      if (p === 0) return { name: "No Memory", description: "Ignores past price movements", emoji: "🚫", context: "Pure trend following" };
      if (p === 1) return { name: "Recent Memory", description: "Uses yesterday's price to predict today", emoji: "📈", context: "Good for momentum patterns" };
      if (p <= 3) return { name: "Short Memory", description: "Considers last few days of price action", emoji: "🔄", context: "Captures short-term cycles" };
      return { name: "Long Memory", description: "Remembers many days of price history", emoji: "🧠", context: "Complex pattern recognition" };
    };

    const getDifferencingDescription = (d: number) => {
      if (d === 0) return { name: "Raw Prices", description: "Uses actual stock prices directly", emoji: "💰", stability: "🔴 May be unstable for trending stocks" };
      if (d === 1) return { name: "Price Changes", description: "Uses daily price change instead of actual prices", emoji: "📊", stability: "🟢 Stable for most stocks" };
      return { name: "Change-of-Changes", description: "Uses acceleration/deceleration of price movements", emoji: "🎢", stability: "🟡 Advanced, use with caution" };
    };

    const getMAOrderDescription = (q: number) => {
      if (q === 0) return { name: "No Smoothing", description: "No error correction from past mistakes", emoji: "📍", context: "Direct prediction" };
      if (q === 1) return { name: "Error Correction", description: "Learns from yesterday's prediction errors", emoji: "🎯", context: "Self-correcting model" };
      if (q <= 3) return { name: "Multi-Error Learning", description: "Learns from several days of prediction errors", emoji: "🔧", context: "Fine-tuned adjustments" };
      return { name: "Complex Error Handling", description: "Advanced error pattern recognition", emoji: "⚙️", context: "Sophisticated correction" };
    };

    const getSeasonalDescription = (seasonal: boolean, m: number) => {
      if (!seasonal) return { name: "No Seasonality", description: "Assumes no repeating patterns", emoji: "📈" };
      if (m <= 7) return { name: "Weekly Patterns", description: `Looks for patterns every ${m} days`, emoji: "📅", example: "Monday effect, weekend gaps" };
      if (m <= 30) return { name: "Monthly Cycles", description: `Detects patterns every ${m} days`, emoji: "🗓️", example: "End-of-month trading, earnings cycles" };
      return { name: "Long-term Cycles", description: `Seasonal patterns every ${m} days`, emoji: "🔄", example: "Quarterly effects, annual trends" };
    };

    const getForecastQualityDescription = (p: number, d: number, q: number) => {
      const complexity = p + d + q;
      if (complexity <= 2) return { level: "Simple", quality: "🟢 Reliable", description: "Basic but robust predictions", speed: "Very fast" };
      if (complexity <= 4) return { level: "Moderate", quality: "🟡 Balanced", description: "Good accuracy with stability", speed: "Fast" };
      return { level: "Complex", quality: "🔴 May overfit", description: "High accuracy but potentially unstable", speed: "Slower" };
    };

    const arDesc = getAROrderDescription(settings.p);
    const diffDesc = getDifferencingDescription(settings.d);
    const maDesc = getMAOrderDescription(settings.q);
    const seasonalDesc = getSeasonalDescription(settings.seasonal, settings.m);
    const qualityDesc = getForecastQualityDescription(settings.p, settings.d, settings.q);

    return (
      <TooltipProvider>
        <div className="space-y-6 mt-4">
          {/* Model Quality Overview */}
          <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">ARIMA Configuration: ({settings.p},{settings.d},{settings.q})</h3>
              <span className="text-sm font-mono bg-gray-200 px-3 py-1 rounded-full">
                {qualityDesc.level} Model
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="font-medium">{qualityDesc.quality}</span>
              <span className="text-gray-600">{qualityDesc.description}</span>
              <span className="text-xs bg-white px-2 py-1 rounded">⚡ {qualityDesc.speed}</span>
            </div>
          </div>

          {/* Auto-Regression (p) - Memory Component */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Price Memory (Auto-Regression)</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How many previous days of prices the model remembers to make predictions.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-blue-100 px-2 py-1 rounded">
                p = {settings.p}
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{arDesc.emoji}</span>
                <span className="font-semibold text-blue-900">{arDesc.name}</span>
              </div>
              <p className="text-sm text-blue-800 mb-1">{arDesc.description}</p>
              <p className="text-xs text-blue-600">Best for: {arDesc.context}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>🚫 No memory</span>
                <span>Complex patterns 🧠</span>
              </div>
              <Slider
                value={[settings.p]}
                min={0}
                max={5}
                step={1}
                onValueChange={(value) => updateARIMASettings({ p: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0</span>
                <span>1</span>
                <span>2</span>
                <span>3</span>
                <span>4</span>
                <span>5</span>
              </div>
            </div>
          </div>

          {/* Differencing (d) - Trend Stability */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Trend Stability (Differencing)</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How many times to "difference" the data to make it stationary. Usually 1 is enough.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-green-100 px-2 py-1 rounded">
                d = {settings.d}
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{diffDesc.emoji}</span>
                <span className="font-semibold text-green-900">{diffDesc.name}</span>
              </div>
              <p className="text-sm text-green-800">{diffDesc.description}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>💰 Raw prices</span>
                <span>Advanced 🎢</span>
              </div>
              <Slider
                value={[settings.d]}
                min={0}
                max={2}
                step={1}
                onValueChange={(value) => updateARIMASettings({ d: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0</span>
                <span>1</span>
                <span>2</span>
              </div>
            </div>
          </div>

          {/* Moving Average (q) - Error Correction */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Error Learning (Moving Average)</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How many days of prediction errors the model learns from to improve future predictions.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-purple-100 px-2 py-1 rounded">
                q = {settings.q}
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{maDesc.emoji}</span>
                <span className="font-semibold text-purple-900">{maDesc.name}</span>
              </div>
              <p className="text-sm text-purple-800 mb-1">{maDesc.description}</p>
              <p className="text-xs text-purple-600">Best for: {maDesc.context}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>📍 Direct</span>
                <span>Complex correction ⚙️</span>
              </div>
              <Slider
                value={[settings.q]}
                min={0}
                max={5}
                step={1}
                onValueChange={(value) => updateARIMASettings({ q: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0</span>
                <span>1</span>
                <span>2</span>
                <span>3</span>
                <span>4</span>
                <span>5</span>
              </div>
            </div>
          </div>

          {/* Seasonal Component */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Seasonal Patterns</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">Detect repeating patterns like weekly effects or monthly cycles in the stock price.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Switch
                checked={settings.seasonal}
                onCheckedChange={(checked) => updateARIMASettings({ seasonal: checked })}
              />
            </div>
            
            <div className={`bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-3 ${!settings.seasonal ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{seasonalDesc.emoji}</span>
                <span className="font-semibold text-orange-900">{seasonalDesc.name}</span>
              </div>
              <p className="text-sm text-orange-800 mb-1">{seasonalDesc.description}</p>
              {seasonalDesc.example && (
                <p className="text-xs text-orange-600">Examples: {seasonalDesc.example}</p>
              )}
            </div>

            {settings.seasonal && (
              <div className="space-y-4 mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm">Season Length (days)</Label>
                    <span className="text-sm font-mono">{settings.m} days</span>
                  </div>
                  <Slider
                    value={[settings.m]}
                    min={2}
                    max={252}
                    step={1}
                    onValueChange={(value) => updateARIMASettings({ m: value[0] })}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>2 (2-day cycle)</span>
                    <span>7 (weekly)</span>
                    <span>30 (monthly)</span>
                    <span>252 (yearly)</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Seasonal Memory (P)</Label>
                    <Slider
                      value={[settings.P]}
                      min={0}
                      max={2}
                      step={1}
                      onValueChange={(value) => updateARIMASettings({ P: value[0] })}
                    />
                    <div className="text-center text-xs text-gray-500">{settings.P}</div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs">Seasonal Trend (D)</Label>
                    <Slider
                      value={[settings.D]}
                      min={0}
                      max={1}
                      step={1}
                      onValueChange={(value) => updateARIMASettings({ D: value[0] })}
                    />
                    <div className="text-center text-xs text-gray-500">{settings.D}</div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs">Seasonal Error (Q)</Label>
                    <Slider
                      value={[settings.Q]}
                      min={0}
                      max={2}
                      step={1}
                      onValueChange={(value) => updateARIMASettings({ Q: value[0] })}
                    />
                    <div className="text-center text-xs text-gray-500">{settings.Q}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Forecast Settings */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Prediction Settings</Label>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs">Forecast Days</Label>
                  <span className="text-xs font-mono">{settings.forecastHorizon || 5} days</span>
                </div>
                <Slider
                  value={[settings.forecastHorizon || 5]}
                  min={1}
                  max={30}
                  step={1}
                  onValueChange={(value) => updateARIMASettings({ forecastHorizon: value[0] })}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs">Confidence Level</Label>
                  <span className="text-xs font-mono">{((settings.confidenceLevel || 0.95) * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[settings.confidenceLevel || 0.95]}
                  min={0.80}
                  max={0.99}
                  step={0.01}
                  onValueChange={(value) => updateARIMASettings({ confidenceLevel: value[0] })}
                />
              </div>
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  };

  const renderXGBoostParameters = () => {
    const settings = model.xgboostSettings || {
      predictMode: 'classification',
      featureWindow: 10,
      predictionHorizon: 1,
      n_estimators: 100,
      learning_rate: 0.1,
      max_depth: 3,
      subsample: 0.8,
      colsample_bytree: 0.8,
      gamma: 0,
      objective: 'binary:logistic'
    };

    // Helper functions for user-friendly descriptions
    const getPredictionModeDescription = (objective: string) => {
      const modes = {
        'binary:logistic': { 
          name: "Direction Prediction", 
          emoji: "↗️", 
          description: "Predicts if prices will go UP or DOWN", 
          bestFor: "Clear buy/sell signals",
          output: "Up/Down probability"
        },
        'reg:squarederror': { 
          name: "Price Prediction", 
          emoji: "📊", 
          description: "Predicts exact future price values", 
          bestFor: "Precise target pricing",
          output: "Dollar amount"
        }
      };
      return modes[objective as keyof typeof modes] || modes['binary:logistic'];
    };

    const getFeatureWindowDescription = (window: number) => {
      if (window <= 5) return { name: "Short-Term Patterns", description: "Focuses on recent price action and momentum", timeframe: "1 week lookback", bestFor: "Day trading, quick moves" };
      if (window <= 15) return { name: "Medium-Term Trends", description: "Balances recent and historical patterns", timeframe: "2-3 weeks lookback", bestFor: "Swing trading" };
      return { name: "Long-Term Cycles", description: "Considers extended market patterns", timeframe: "1+ month lookback", bestFor: "Position trading" };
    };

    const getTreeComplexityDescription = (trees: number, depth: number) => {
      const complexity = trees * depth;
      if (complexity <= 300) return { level: "Simple", risk: "🟢 Low Overfitting", description: "Fast, robust predictions", training: "Quick training" };
      if (complexity <= 600) return { level: "Moderate", risk: "🟡 Balanced", description: "Good accuracy vs stability", training: "Medium training" };
      return { level: "Complex", risk: "🔴 Overfitting Risk", description: "High accuracy but may be unstable", training: "Slow training" };
    };

    const getLearningRateDescription = (rate: number) => {
      if (rate <= 0.05) return { name: "Cautious Learning", description: "Conservative, steady improvement", speed: "Slow but stable", bestFor: "Noisy data" };
      if (rate <= 0.15) return { name: "Balanced Learning", description: "Good balance of speed and stability", speed: "Moderate pace", bestFor: "Most scenarios" };
      return { name: "Aggressive Learning", description: "Fast learning but may overshoot", speed: "Quick convergence", bestFor: "Clean, strong patterns" };
    };

    const modeDesc = getPredictionModeDescription(settings.objective);
    const windowDesc = getFeatureWindowDescription(settings.featureWindow || 10);
    const complexityDesc = getTreeComplexityDescription(settings.n_estimators, settings.max_depth);
    const learningDesc = getLearningRateDescription(settings.learning_rate);

    return (
      <TooltipProvider>
        <div className="space-y-6 mt-4">
          {/* Prediction Mode */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-sm font-medium">Prediction Type</Label>
              <span className="text-sm font-mono bg-blue-100 px-2 py-1 rounded">
                {modeDesc.name}
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{modeDesc.emoji}</span>
                <span className="font-semibold text-blue-900">{modeDesc.name}</span>
              </div>
              <p className="text-sm text-blue-800 mb-1">{modeDesc.description}</p>
              <div className="flex justify-between text-xs text-blue-600">
                <span>Best for: {modeDesc.bestFor}</span>
                <span>Output: {modeDesc.output}</span>
              </div>
            </div>
            
            <Select
              value={settings.objective}
              onValueChange={(value) => updateXGBoostSettings({ objective: value })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select prediction type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="binary:logistic">
                  <div className="flex items-center gap-2">
                    <span>↗️</span>
                    <span>Direction Prediction (Up/Down)</span>
                  </div>
                </SelectItem>
                <SelectItem value="reg:squarederror">
                  <div className="flex items-center gap-2">
                    <span>📊</span>
                    <span>Price Prediction (Exact Values)</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Feature Window - Pattern Recognition Scope */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Pattern Recognition Scope</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How many days of price history the AI analyzes to find patterns.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-green-100 px-2 py-1 rounded">
                {settings.featureWindow || 10} days
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-green-900">{windowDesc.name}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{windowDesc.timeframe}</span>
              </div>
              <p className="text-sm text-green-800 mb-1">{windowDesc.description}</p>
              <p className="text-xs text-green-600">Best for: {windowDesc.bestFor}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>📅 Short-term</span>
                <span>Long-term 📚</span>
              </div>
              <Slider
                value={[settings.featureWindow || 10]}
                min={5}
                max={30}
                step={1}
                onValueChange={(value) => updateXGBoostSettings({ featureWindow: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>5</span>
                <span>15</span>
                <span>30</span>
              </div>
            </div>
          </div>

          {/* Model Complexity */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Model Complexity</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">Higher complexity can find more patterns but may overfit to training data.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-purple-100 px-2 py-1 rounded">
                {complexityDesc.level}
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-purple-900">{complexityDesc.level} Model</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{complexityDesc.risk}</span>
              </div>
              <p className="text-sm text-purple-800 mb-1">{complexityDesc.description}</p>
              <div className="flex justify-between text-xs text-purple-600">
                <span>Trees: {settings.n_estimators}</span>
                <span>Depth: {settings.max_depth}</span>
                <span>{complexityDesc.training}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-gray-600 mb-1 block">Number of Trees</Label>
                <Slider
                  value={[settings.n_estimators]}
                  min={50}
                  max={300}
                  step={10}
                  onValueChange={(value) => updateXGBoostSettings({ n_estimators: value[0] })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>50</span>
                  <span>300</span>
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-gray-600 mb-1 block">Tree Depth</Label>
                <Slider
                  value={[settings.max_depth]}
                  min={2}
                  max={8}
                  step={1}
                  onValueChange={(value) => updateXGBoostSettings({ max_depth: value[0] })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>2</span>
                  <span>8</span>
                </div>
              </div>
            </div>
          </div>

          {/* Learning Speed */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Learning Speed</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How quickly the model learns from data. Slower learning is more stable but takes longer to train.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-orange-100 px-2 py-1 rounded">
                {(settings.learning_rate * 100).toFixed(0)}%
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-orange-900">{learningDesc.name}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{learningDesc.speed}</span>
              </div>
              <p className="text-sm text-orange-800 mb-1">{learningDesc.description}</p>
              <p className="text-xs text-orange-600">Best for: {learningDesc.bestFor}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>🐌 Cautious</span>
                <span>Aggressive 🚀</span>
              </div>
              <Slider
                value={[settings.learning_rate]}
                min={0.01}
                max={0.3}
                step={0.01}
                onValueChange={(value) => updateXGBoostSettings({ learning_rate: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1%</span>
                <span>10%</span>
                <span>30%</span>
              </div>
            </div>
          </div>

          {/* Additional Settings */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Fine-Tuning Controls</Label>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs">Data Sampling</Label>
                  <span className="text-xs font-mono">{(settings.subsample * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[settings.subsample]}
                  min={0.5}
                  max={1.0}
                  step={0.05}
                  onValueChange={(value) => updateXGBoostSettings({ subsample: value[0] })}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs">Feature Sampling</Label>
                  <span className="text-xs font-mono">{(settings.colsample_bytree * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[settings.colsample_bytree]}
                  min={0.3}
                  max={1.0}
                  step={0.05}
                  onValueChange={(value) => updateXGBoostSettings({ colsample_bytree: value[0] })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Regularization (Gamma)</Label>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-3 w-3 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">Minimum loss reduction required to make a split. Higher values prevent overfitting.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <span className="text-xs font-mono">{settings.gamma.toFixed(1)}</span>
              </div>
              <Slider
                value={[settings.gamma]}
                min={0}
                max={2.0}
                step={0.1}
                onValueChange={(value) => updateXGBoostSettings({ gamma: value[0] })}
              />
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  };

  const renderStewieGPTParameters = () => {
    const settings = model.stewieGPTSettings || {
      promptTemplate: "Analyze the following market data and news for {symbol}:\n{data}\n{news}\nProvide buy/sell signal with confidence score.",
      maxOutputTokens: 500,
      temperature: 0.3,
      confidenceThreshold: 0.7,
      volatilityThreshold: 0.1,
      newsSourceFilter: ['Bloomberg', 'Reuters', 'CNBC']
    };

    return (
      <div className="space-y-4 mt-3">
        <div>
          <Label className="text-xs">Confidence Threshold</Label>
          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-gray-500">Lower</span>
            <span className="text-xs font-mono">{settings.confidenceThreshold.toFixed(2)}</span>
            <span className="text-xs text-gray-500">Higher</span>
          </div>
          <Slider
            value={[settings.confidenceThreshold]}
            min={0.5}
            max={0.9}
            step={0.05}
            onValueChange={(value) => updateFinGPTSettings({ confidenceThreshold: value[0] })}
          />
        </div>

        <div>
          <Label className="text-xs">Temperature</Label>
          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-gray-500">Deterministic</span>
            <span className="text-xs font-mono">{settings.temperature.toFixed(2)}</span>
            <span className="text-xs text-gray-500">Creative</span>
          </div>
          <Slider
            value={[settings.temperature]}
            min={0.1}
            max={1.5}
            step={0.1}
            onValueChange={(value) => updateFinGPTSettings({ temperature: value[0] })}
          />
        </div>

        <div>
          <Label className="text-xs">Volatility Threshold</Label>
          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-gray-500">Stable</span>
            <span className="text-xs font-mono">{settings.volatilityThreshold.toFixed(2)}</span>
            <span className="text-xs text-gray-500">Volatile</span>
          </div>
          <Slider
            value={[settings.volatilityThreshold]}
            min={0.05}
            max={0.3}
            step={0.01}
            onValueChange={(value) => updateFinGPTSettings({ volatilityThreshold: value[0] })}
          />
        </div>

        <div>
          <Label className="text-xs">Max Output Tokens</Label>
          <div className="mt-1 w-1/2">
            <Input 
              type="number" 
              className="text-xs h-7" 
              value={settings.maxOutputTokens}
              min={100}
              max={2000}
              step={100}
              onChange={(e) => updateFinGPTSettings({ maxOutputTokens: parseInt(e.target.value) || 500 })}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderMonteCarloParameters = () => {
    const settings = model.monteCarloSettings || {
      simulationCount: 1000,
      timeHorizon: 30,
      distribution: 'normal',
      includeTailRisks: true,
      confidenceInterval: 0.95,
      volShockEnabled: false,
      volShockMagnitude: 2.0
    };

    return (
      <div className="space-y-4 mt-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Number of Simulations</Label>
            <div className="mt-1">
              <Input 
                type="number" 
                className="text-xs h-7" 
                value={settings.simulationCount}
                min={100}
                max={10000}
                step={100}
                onChange={(e) => updateMonteCarloSettings({ simulationCount: parseInt(e.target.value) || 1000 })}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Time Horizon (days)</Label>
            <div className="mt-1">
              <Input 
                type="number" 
                className="text-xs h-7" 
                value={settings.timeHorizon}
                min={1}
                max={365}
                onChange={(e) => updateMonteCarloSettings({ timeHorizon: parseInt(e.target.value) || 30 })}
              />
            </div>
          </div>
        </div>

        <div>
          <Label className="text-xs">Distribution Type</Label>
          <div className="mt-1">
            <Select
              value={settings.distribution}
              onValueChange={(value) => updateMonteCarloSettings({ distribution: value })}
            >
              <SelectTrigger className="text-xs h-7">
                <SelectValue placeholder="Select distribution" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="fat-tailed">Fat-Tailed</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-xs">Confidence Interval</Label>
          <div className="mt-1 w-24">
            <Select
              value={settings.confidenceInterval.toString()}
              onValueChange={(value) => updateMonteCarloSettings({ confidenceInterval: parseFloat(value) })}
            >
              <SelectTrigger className="text-xs h-7">
                <SelectValue placeholder="Select interval" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0.90">90%</SelectItem>
                <SelectItem value="0.95">95%</SelectItem>
                <SelectItem value="0.99">99%</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="includeTailRisks"
            checked={settings.includeTailRisks}
            onCheckedChange={(checked) => updateMonteCarloSettings({ includeTailRisks: checked })}
          />
          <Label htmlFor="includeTailRisks" className="text-xs">Include Tail Risk Events</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="volShockEnabled"
            checked={settings.volShockEnabled}
            onCheckedChange={(checked) => updateMonteCarloSettings({ volShockEnabled: checked })}
          />
          <Label htmlFor="volShockEnabled" className="text-xs">Enable Volatility Shocks</Label>
        </div>

        {settings.volShockEnabled && (
          <div>
            <Label className="text-xs">Volatility Shock Magnitude</Label>
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-gray-500">Low</span>
              <span className="text-xs font-mono">{settings.volShockMagnitude.toFixed(1)}x</span>
              <span className="text-xs text-gray-500">High</span>
            </div>
            <Slider
              value={[settings.volShockMagnitude]}
              min={1.1}
              max={5.0}
              step={0.1}
              onValueChange={(value) => updateMonteCarloSettings({ volShockMagnitude: value[0] })}
            />
          </div>
        )}
      </div>
    );
  };

  const renderDijkstraParameters = () => {
    const settings = model.dijkstraSettings || {
      pathOptimization: 'combined',
      riskWeight: 0.5,
      returnWeight: 0.5,
      includeFees: true,
      maxIterations: 100
    };

    return (
      <div className="space-y-4 mt-3">
        <div>
          <Label className="text-xs">Path Optimization Goal</Label>
          <div className="mt-1">
            <Select
              value={settings.pathOptimization}
              onValueChange={(value) => updateDijkstraSettings({ pathOptimization: value })}
            >
              <SelectTrigger className="text-xs h-7">
                <SelectValue placeholder="Select optimization goal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="risk">Minimize Risk</SelectItem>
                <SelectItem value="return">Maximize Return</SelectItem>
                <SelectItem value="combined">Risk-Return Balance</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {settings.pathOptimization === 'combined' && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Risk Weight</Label>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs font-mono">{settings.riskWeight.toFixed(2)}</span>
              </div>
              <Slider
                value={[settings.riskWeight]}
                min={0}
                max={1}
                step={0.05}
                onValueChange={(value) => {
                  const riskWeight = value[0];
                  updateDijkstraSettings({ 
                    riskWeight,
                    returnWeight: 1 - riskWeight
                  });
                }}
              />
            </div>

            <div>
              <Label className="text-xs">Return Weight</Label>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs font-mono">{settings.returnWeight.toFixed(2)}</span>
              </div>
              <Slider
                value={[settings.returnWeight]}
                min={0}
                max={1}
                step={0.05}
                onValueChange={(value) => {
                  const returnWeight = value[0];
                  updateDijkstraSettings({ 
                    returnWeight,
                    riskWeight: 1 - returnWeight
                  });
                }}
              />
            </div>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <Switch
            id="includeFees"
            checked={settings.includeFees}
            onCheckedChange={(checked) => updateDijkstraSettings({ includeFees: checked })}
          />
          <Label htmlFor="includeFees" className="text-xs">Include Trading Fees in Calculations</Label>
        </div>

        <div>
          <Label className="text-xs">Maximum Iterations</Label>
          <div className="mt-1 w-32">
            <Input 
              type="number" 
              className="text-xs h-7" 
              value={settings.maxIterations}
              min={10}
              max={1000}
              step={10}
              onChange={(e) => updateDijkstraSettings({ maxIterations: parseInt(e.target.value) || 100 })}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderRLDQNParameters = () => {
    const settings = model.rlDqnSettings || {
      timesteps: 10000,
      learning_rate: 0.001,
      gamma: 0.99,
      risk_aversion_param: 0.1,
      generate_signal_with_conviction: false,
      buy_conviction_threshold: 0.1,
      sell_conviction_threshold: 0.1,
      exploration_rate: 0.1,
      memory_size: 10000,
      batch_size: 32
    };

    // Helper functions for user-friendly descriptions
    const getTrainingIntensityDescription = (timesteps: number) => {
      if (timesteps <= 5000) return { 
        name: "Quick Training", 
        description: "Basic trading pattern learning", 
        emoji: "⚡", 
        duration: "Minutes", 
        quality: "Good for simple strategies",
        experience: "Limited market experience"
      };
      if (timesteps <= 50000) return { 
        name: "Standard Training", 
        description: "Thorough market pattern mastery", 
        emoji: "🎯", 
        duration: "Hours", 
        quality: "Balanced performance",
        experience: "Moderate market experience"
      };
      return { 
        name: "Deep Training", 
        description: "Extensive market scenario learning", 
        emoji: "🧠", 
        duration: "Many hours", 
        quality: "High performance potential",
        experience: "Deep market experience"
      };
    };

    const getLearningSpeedDescription = (rate: number) => {
      if (rate <= 0.0001) return { name: "Conservative Learner", description: "Very slow but stable learning", emoji: "🐌", stability: "High stability", risk: "Low overfitting" };
      if (rate <= 0.001) return { name: "Balanced Learner", description: "Steady learning with good stability", emoji: "⚖️", stability: "Good balance", risk: "Moderate overfitting" };
      if (rate <= 0.01) return { name: "Aggressive Learner", description: "Fast learning but may be unstable", emoji: "🚀", stability: "Lower stability", risk: "Higher overfitting" };
      return { name: "Risk Taker", description: "Very fast learning, high instability risk", emoji: "⚡", stability: "Unstable", risk: "High overfitting" };
    };

    const getPatienceDescription = (gamma: number) => {
      if (gamma <= 0.9) return { name: "Impatient Trader", description: "Focuses on immediate profits", emoji: "💨", timeframe: "Short-term focus", behavior: "Quick gains priority" };
      if (gamma <= 0.95) return { name: "Balanced Trader", description: "Considers both short and long-term gains", emoji: "⚖️", timeframe: "Medium-term focus", behavior: "Balanced approach" };
      if (gamma <= 0.99) return { name: "Patient Trader", description: "Values long-term strategy over quick wins", emoji: "🕰️", timeframe: "Long-term focus", behavior: "Strategic patience" };
      return { name: "Long-term Investor", description: "Extremely patient, focuses on future rewards", emoji: "🏛️", timeframe: "Very long-term", behavior: "Maximum patience" };
    };

    const getRiskPersonalityDescription = (aversion: number) => {
      if (aversion <= 0.05) return { name: "Risk Seeker", description: "Comfortable with high-risk trades", emoji: "🎲", personality: "Aggressive", approach: "High risk tolerance" };
      if (aversion <= 0.15) return { name: "Moderate Risk Taker", description: "Balanced approach to risk", emoji: "🎯", personality: "Balanced", approach: "Calculated risks" };
      if (aversion <= 0.3) return { name: "Risk Averse", description: "Prefers safer trading strategies", emoji: "🛡️", personality: "Conservative", approach: "Safety first" };
      return { name: "Ultra Conservative", description: "Extremely cautious with risk", emoji: "🏦", personality: "Very conservative", approach: "Minimal risk tolerance" };
    };

    const getExplorationDescription = (rate: number) => {
      if (rate <= 0.05) return { name: "Routine Trader", description: "Sticks to proven strategies", emoji: "🔄", behavior: "Exploitation focus", discovery: "Limited exploration" };
      if (rate <= 0.15) return { name: "Curious Trader", description: "Occasionally tries new strategies", emoji: "🔍", behavior: "Balanced approach", discovery: "Moderate exploration" };
      if (rate <= 0.3) return { name: "Adventurous Trader", description: "Frequently explores new opportunities", emoji: "🗺️", behavior: "Exploration focus", discovery: "High exploration" };
      return { name: "Experimental Trader", description: "Constantly seeking new strategies", emoji: "🧪", behavior: "Maximum exploration", discovery: "Extreme exploration" };
    };

    const getConvictionDescription = (buyThreshold: number, sellThreshold: number) => {
      const avgThreshold = (buyThreshold + sellThreshold) / 2;
      if (avgThreshold <= 0.05) return { level: "Hair Trigger", description: "Acts on smallest signals", emoji: "⚡", sensitivity: "Extremely sensitive", frequency: "Very frequent trades" };
      if (avgThreshold <= 0.15) return { level: "Responsive", description: "Acts on moderate confidence", emoji: "📊", sensitivity: "Moderately sensitive", frequency: "Regular trades" };
      if (avgThreshold <= 0.3) return { level: "Selective", description: "Requires good confidence to act", emoji: "🎯", sensitivity: "Selective", frequency: "Measured trades" };
      return { level: "Ultra Selective", description: "Only acts on highest confidence", emoji: "💎", sensitivity: "Very selective", frequency: "Rare, high-conviction trades" };
    };

    const trainingDesc = getTrainingIntensityDescription(settings.timesteps);
    const learningDesc = getLearningSpeedDescription(settings.learning_rate);
    const patienceDesc = getPatienceDescription(settings.gamma);
    const riskDesc = getRiskPersonalityDescription(settings.risk_aversion_param);
    const exploreDesc = getExplorationDescription(settings.exploration_rate || 0.1);
    const convictionDesc = getConvictionDescription(settings.buy_conviction_threshold, settings.sell_conviction_threshold);

    return (
      <TooltipProvider>
        <div className="space-y-6 mt-4">
          {/* Agent Overview */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-purple-900">Reinforcement Learning Agent</h3>
              <span className="text-sm font-mono bg-purple-200 px-3 py-1 rounded-full">
                AI Trader
              </span>
            </div>
            <div className="text-sm text-purple-800">
              An AI agent that learns to trade through trial and error, developing its own trading strategy by experiencing market rewards and penalties.
            </div>
          </div>

          {/* Training Intensity */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Training Intensity (Market Experience)</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How many market scenarios the AI experiences during training. More timesteps = more experience but longer training.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-blue-100 px-2 py-1 rounded">
                {(settings.timesteps / 1000).toFixed(0)}k steps
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{trainingDesc.emoji}</span>
                <span className="font-semibold text-blue-900">{trainingDesc.name}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">⏱️ {trainingDesc.duration}</span>
              </div>
              <p className="text-sm text-blue-800 mb-1">{trainingDesc.description}</p>
              <div className="flex justify-between text-xs text-blue-600">
                <span>Quality: {trainingDesc.quality}</span>
                <span>{trainingDesc.experience}</span>
              </div>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>⚡ Quick learning</span>
                <span>Deep experience 🧠</span>
              </div>
              <Slider
                value={[settings.timesteps]}
                min={1000}
                max={100000}
                step={1000}
                onValueChange={(value) => updateRLDQNSettings({ timesteps: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1k</span>
                <span>10k</span>
                <span>50k</span>
                <span>100k</span>
              </div>
            </div>
          </div>

          {/* Learning Personality */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Learning Speed (Adaptation Rate)</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How quickly the AI updates its strategy from new experiences. Higher = faster learning but more unstable.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-green-100 px-2 py-1 rounded">
                {settings.learning_rate.toFixed(4)}
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{learningDesc.emoji}</span>
                <span className="font-semibold text-green-900">{learningDesc.name}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{learningDesc.stability}</span>
              </div>
              <p className="text-sm text-green-800 mb-1">{learningDesc.description}</p>
              <p className="text-xs text-green-600">Risk: {learningDesc.risk}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>🐌 Conservative</span>
                <span>Aggressive 🚀</span>
              </div>
              <Slider
                value={[settings.learning_rate]}
                min={0.00001}
                max={0.1}
                step={0.00001}
                onValueChange={(value) => updateRLDQNSettings({ learning_rate: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0.0001</span>
                <span>0.001</span>
                <span>0.01</span>
                <span>0.1</span>
              </div>
            </div>
          </div>

          {/* Time Horizon Preference */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Time Horizon (Trading Patience)</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How much the AI values future rewards vs immediate gains. Higher = more patient, longer-term focus.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-purple-100 px-2 py-1 rounded">
                γ = {settings.gamma.toFixed(3)}
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{patienceDesc.emoji}</span>
                <span className="font-semibold text-purple-900">{patienceDesc.name}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{patienceDesc.timeframe}</span>
              </div>
              <p className="text-sm text-purple-800 mb-1">{patienceDesc.description}</p>
              <p className="text-xs text-purple-600">{patienceDesc.behavior}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>💨 Impatient</span>
                <span>Patient 🏛️</span>
              </div>
              <Slider
                value={[settings.gamma]}
                min={0.8}
                max={0.999}
                step={0.001}
                onValueChange={(value) => updateRLDQNSettings({ gamma: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0.80</span>
                <span>0.95</span>
                <span>0.99</span>
                <span>0.999</span>
              </div>
            </div>
          </div>

          {/* Risk Personality */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Risk Personality</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How much the AI is penalized for risky trades during training. Higher = more conservative trader.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-orange-100 px-2 py-1 rounded">
                {(settings.risk_aversion_param * 100).toFixed(1)}%
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{riskDesc.emoji}</span>
                <span className="font-semibold text-orange-900">{riskDesc.name}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{riskDesc.personality}</span>
              </div>
              <p className="text-sm text-orange-800 mb-1">{riskDesc.description}</p>
              <p className="text-xs text-orange-600">{riskDesc.approach}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>🎲 Risk seeker</span>
                <span>Conservative 🏦</span>
              </div>
              <Slider
                value={[settings.risk_aversion_param]}
                min={0.0}
                max={0.5}
                step={0.01}
                onValueChange={(value) => updateRLDQNSettings({ risk_aversion_param: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0%</span>
                <span>10%</span>
                <span>30%</span>
                <span>50%</span>
              </div>
            </div>
          </div>

          {/* Exploration Strategy */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Exploration Strategy</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How often the AI tries new strategies vs sticking to proven ones. Higher = more experimental.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-teal-100 px-2 py-1 rounded">
                {((settings.exploration_rate || 0.1) * 100).toFixed(0)}%
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{exploreDesc.emoji}</span>
                <span className="font-semibold text-teal-900">{exploreDesc.name}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{exploreDesc.behavior}</span>
              </div>
              <p className="text-sm text-teal-800 mb-1">{exploreDesc.description}</p>
              <p className="text-xs text-teal-600">{exploreDesc.discovery}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>🔄 Routine</span>
                <span>Experimental 🧪</span>
              </div>
              <Slider
                value={[settings.exploration_rate || 0.1]}
                min={0.0}
                max={0.5}
                step={0.01}
                onValueChange={(value) => updateRLDQNSettings({ exploration_rate: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0%</span>
                <span>10%</span>
                <span>30%</span>
                <span>50%</span>
              </div>
            </div>
          </div>

          {/* Signal Generation */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Advanced Signal Generation</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">Use the AI's confidence level (Q-values) to determine trading signals instead of simple buy/sell decisions.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Switch
                checked={settings.generate_signal_with_conviction}
                onCheckedChange={(checked) => updateRLDQNSettings({ generate_signal_with_conviction: checked })}
              />
            </div>
            
            {settings.generate_signal_with_conviction && (
              <div className="space-y-4 mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{convictionDesc.emoji}</span>
                    <span className="font-semibold text-gray-900">{convictionDesc.level} Trader</span>
                    <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{convictionDesc.sensitivity}</span>
                  </div>
                  <p className="text-sm text-gray-800 mb-1">{convictionDesc.description}</p>
                  <p className="text-xs text-gray-600">{convictionDesc.frequency}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs">Buy Conviction Threshold</Label>
                      <span className="text-xs font-mono">{(settings.buy_conviction_threshold * 100).toFixed(0)}%</span>
                    </div>
                    <Slider
                      value={[settings.buy_conviction_threshold]}
                      min={0.0}
                      max={1.0}
                      step={0.01}
                      onValueChange={(value) => updateRLDQNSettings({ buy_conviction_threshold: value[0] })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs">Sell Conviction Threshold</Label>
                      <span className="text-xs font-mono">{(settings.sell_conviction_threshold * 100).toFixed(0)}%</span>
                    </div>
                    <Slider
                      value={[settings.sell_conviction_threshold]}
                      min={0.0}
                      max={1.0}
                      step={0.01}
                      onValueChange={(value) => updateRLDQNSettings({ sell_conviction_threshold: value[0] })}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Advanced Training Settings */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Advanced Training Settings</Label>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs">Memory Size</Label>
                  <span className="text-xs font-mono">{((settings.memory_size || 10000) / 1000).toFixed(0)}k</span>
                </div>
                <Slider
                  value={[settings.memory_size || 10000]}
                  min={1000}
                  max={50000}
                  step={1000}
                  onValueChange={(value) => updateRLDQNSettings({ memory_size: value[0] })}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs">Batch Size</Label>
                  <span className="text-xs font-mono">{settings.batch_size || 32}</span>
                </div>
                <Slider
                  value={[settings.batch_size || 32]}
                  min={8}
                  max={128}
                  step={8}
                  onValueChange={(value) => updateRLDQNSettings({ batch_size: value[0] })}
                />
              </div>
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  };

  const renderHDKParameters = () => {
    const settings = model.hdkSettings || {
      kernelType: 'rbf',
      dimensions: 5,
      learningRate: 0.01,
      regularization: 0.001,
      window: 20,
      gamma: 0.1,
      polyDegree: 3,
      coef0: 0,
      useAdaptiveKernel: true,
      featureNormalization: 'z-score',
      zscoreThreshold: 2.0
    };

    // Helper functions for user-friendly descriptions
    const getKernelTypeDescription = (kernelType: string) => {
      switch (kernelType) {
        case 'rbf':
          return {
            name: "Radial Basis Function (RBF)",
            description: "Similarity-based pattern detection",
            emoji: "🎯",
            complexity: "Medium",
            use_case: "Great for capturing non-linear relationships",
            analogy: "Like recognizing patterns based on how 'similar' they are to known successful trades",
            performance: "Excellent for complex market patterns"
          };
        case 'poly':
          return {
            name: "Polynomial Kernel",
            description: "Mathematical curve fitting",
            emoji: "📈",
            complexity: "High",
            use_case: "Models complex polynomial relationships",
            analogy: "Like fitting mathematical curves to market data trends",
            performance: "Good for trending markets with clear mathematical patterns"
          };
        case 'sigmoid':
          return {
            name: "Sigmoid Kernel",
            description: "Neural network-like activation",
            emoji: "🧠",
            complexity: "High",
            use_case: "Mimics neural network behavior",
            analogy: "Like a simplified neural network decision process",
            performance: "Specialized for binary classification tasks"
          };
        case 'linear':
          return {
            name: "Linear Kernel",
            description: "Simple linear relationships",
            emoji: "📏",
            complexity: "Low",
            use_case: "For when markets behave predictably",
            analogy: "Like drawing straight trend lines through data points",
            performance: "Best for simple, linear market trends"
          };
        default:
          return {
            name: "Unknown Kernel",
            description: "Select a kernel type",
            emoji: "❓",
            complexity: "Unknown",
            use_case: "Please select a kernel",
            analogy: "No kernel selected",
            performance: "N/A"
          };
      }
    };

    const getDimensionalityDescription = (dimensions: number) => {
      if (dimensions <= 3) return { 
        level: "Basic Analysis", 
        description: "Simple 2D/3D pattern recognition", 
        emoji: "📊", 
        complexity: "Low computational cost",
        pattern_scope: "Basic patterns only",
        overfitting_risk: "Low risk"
      };
      if (dimensions <= 8) return { 
        level: "Standard Analysis", 
        description: "Multi-dimensional pattern detection", 
        emoji: "🔍", 
        complexity: "Moderate computational cost",
        pattern_scope: "Good pattern coverage",
        overfitting_risk: "Moderate risk"
      };
      if (dimensions <= 20) return { 
        level: "Advanced Analysis", 
        description: "High-dimensional pattern space", 
        emoji: "🧮", 
        complexity: "High computational cost",
        pattern_scope: "Comprehensive patterns",
        overfitting_risk: "Higher risk"
      };
      return { 
        level: "Expert Analysis", 
        description: "Ultra-high dimensional space", 
        emoji: "🚀", 
        complexity: "Very high computational cost",
        pattern_scope: "Maximum pattern detection",
        overfitting_risk: "Overfitting risk - use carefully"
      };
    };

    const getWindowDescription = (window: number) => {
      if (window <= 10) return { 
        timeframe: "Very Short-term", 
        description: "Focuses on immediate market moves", 
        emoji: "⚡", 
        memory: "Limited historical context",
        reactivity: "Highly reactive to recent changes"
      };
      if (window <= 30) return { 
        timeframe: "Short-term", 
        description: "Captures short-term patterns", 
        emoji: "📈", 
        memory: "Recent market behavior focus",
        reactivity: "Good balance of history and reactivity"
      };
      if (window <= 60) return { 
        timeframe: "Medium-term", 
        description: "Balanced historical perspective", 
        emoji: "⚖️", 
        memory: "Solid historical foundation",
        reactivity: "Balanced approach"
      };
      return { 
        timeframe: "Long-term", 
        description: "Deep historical pattern analysis", 
        emoji: "🏛️", 
        memory: "Extensive historical context",
        reactivity: "Slower to adapt but more stable"
      };
    };

    const getLearningSpeedDescription = (rate: number) => {
      if (rate <= 0.001) return { 
        speed: "Conservative Learner", 
        description: "Slow and steady adaptation", 
        emoji: "🐌", 
        stability: "Very stable",
        risk: "Low overfitting risk"
      };
      if (rate <= 0.01) return { 
        speed: "Balanced Learner", 
        description: "Moderate adaptation rate", 
        emoji: "⚖️", 
        stability: "Good stability",
        risk: "Moderate risk"
      };
      if (rate <= 0.05) return { 
        speed: "Aggressive Learner", 
        description: "Fast adaptation to changes", 
        emoji: "🚀", 
        stability: "Less stable",
        risk: "Higher overfitting risk"
      };
      return { 
        speed: "Risk Taker", 
        description: "Very fast, potentially unstable", 
        emoji: "⚡", 
        stability: "Unstable",
        risk: "High overfitting risk"
      };
    };

    const getRegularizationDescription = (reg: number) => {
      if (reg <= 0.0001) return { 
        level: "Minimal Safety", 
        description: "Little overfitting protection", 
        emoji: "🎲", 
        protection: "Low protection",
        performance: "May overfit but higher potential performance"
      };
      if (reg <= 0.001) return { 
        level: "Balanced Safety", 
        description: "Good overfitting balance", 
        emoji: "🛡️", 
        protection: "Moderate protection",
        performance: "Good balance of performance and stability"
      };
      if (reg <= 0.01) return { 
        level: "Conservative Safety", 
        description: "Strong overfitting prevention", 
        emoji: "🏦", 
        protection: "High protection",
        performance: "More stable but potentially lower performance"
      };
      return { 
        level: "Ultra Conservative", 
        description: "Maximum overfitting prevention", 
        emoji: "🔒", 
        protection: "Maximum protection",
        performance: "Very stable but limited performance potential"
      };
    };

    const getNormalizationDescription = (norm: string) => {
      switch (norm) {
        case 'min-max':
          return {
            name: "Min-Max Scaling",
            description: "Scales all features to 0-1 range",
            emoji: "📐",
            use_case: "When all features should have equal weight",
            analogy: "Like normalizing all indicators to the same scale",
            effect: "All features contribute equally"
          };
        case 'z-score':
          return {
            name: "Z-Score Normalization",
            description: "Centers data around average with standard deviation",
            emoji: "📊",
            use_case: "Preserves the shape of data distribution",
            analogy: "Like expressing everything in terms of 'how many standard deviations from normal'",
            effect: "Maintains relative importance while normalizing"
          };
        case 'none':
          return {
            name: "No Normalization",
            description: "Uses raw data values",
            emoji: "🔢",
            use_case: "When features are already on similar scales",
            analogy: "Like using raw price and volume data directly",
            effect: "Features with larger values dominate"
          };
        default:
          return {
            name: "Unknown Normalization",
            description: "Select a normalization method",
            emoji: "❓",
            use_case: "Please select a method",
            analogy: "No normalization selected",
            effect: "N/A"
          };
      }
    };

    const kernelDesc = getKernelTypeDescription(settings.kernelType);
    const dimensionDesc = getDimensionalityDescription(settings.dimensions);
    const windowDesc = getWindowDescription(settings.window);
    const learningDesc = getLearningSpeedDescription(settings.learningRate);
    const regularizationDesc = getRegularizationDescription(settings.regularization);
    const normalizationDesc = getNormalizationDescription(settings.featureNormalization);

    return (
      <TooltipProvider>
        <div className="space-y-6 mt-4">
          {/* HDK Overview */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-indigo-900">Higher Dimensional Kernel (HDK)</h3>
              <span className="text-sm font-mono bg-indigo-200 px-3 py-1 rounded-full">
                Mathematical Engine
              </span>
            </div>
            <div className="text-sm text-indigo-800">
              Advanced mathematical framework that maps market data into high-dimensional spaces to discover hidden patterns invisible in traditional analysis.
            </div>
          </div>

          {/* Kernel Type Selection */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">Pattern Detection Method (Kernel Type)</Label>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">The mathematical function that determines how the algorithm compares and relates different market patterns.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            
            <Select 
              value={settings.kernelType || 'rbf'} 
              onValueChange={(v) => updateHDKSettings({ kernelType: v as 'rbf' | 'poly' | 'sigmoid' | 'linear' })}
            >
              <SelectTrigger className="text-sm h-10">
                <SelectValue placeholder="Select pattern detection method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rbf">🎯 Radial Basis Function (RBF) - Similarity-based</SelectItem>
                <SelectItem value="poly">📈 Polynomial - Mathematical curves</SelectItem>
                <SelectItem value="sigmoid">🧠 Sigmoid - Neural network-like</SelectItem>
                <SelectItem value="linear">📏 Linear - Simple relationships</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{kernelDesc.emoji}</span>
                <span className="font-semibold text-blue-900">{kernelDesc.name}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">Complexity: {kernelDesc.complexity}</span>
              </div>
              <p className="text-sm text-blue-800 mb-1">{kernelDesc.description}</p>
              <p className="text-xs text-blue-600 mb-1">{kernelDesc.analogy}</p>
              <p className="text-xs text-blue-600 font-medium">{kernelDesc.performance}</p>
            </div>
          </div>

          {/* Dimensionality */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Analysis Dimensions (Complexity)</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How many different aspects of market data to analyze simultaneously. Higher = more complex patterns but greater computational cost.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-purple-100 px-2 py-1 rounded">
                {settings.dimensions}D space
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{dimensionDesc.emoji}</span>
                <span className="font-semibold text-purple-900">{dimensionDesc.level}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{dimensionDesc.complexity}</span>
              </div>
              <p className="text-sm text-purple-800 mb-1">{dimensionDesc.description}</p>
              <div className="flex justify-between text-xs text-purple-600">
                <span>{dimensionDesc.pattern_scope}</span>
                <span>{dimensionDesc.overfitting_risk}</span>
              </div>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>📊 Simple patterns</span>
                <span>Advanced patterns 🚀</span>
              </div>
              <Slider
                value={[settings.dimensions]}
                min={2}
                max={50}
                step={1}
                onValueChange={(value) => updateHDKSettings({ dimensions: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>2D</span>
                <span>8D</span>
                <span>20D</span>
                <span>50D</span>
              </div>
            </div>
          </div>

          {/* Historical Window */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Historical Memory (Window Size)</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How many historical data points to consider when making predictions. Larger window = more historical context but slower adaptation.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-green-100 px-2 py-1 rounded">
                {settings.window} periods
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{windowDesc.emoji}</span>
                <span className="font-semibold text-green-900">{windowDesc.timeframe} Focus</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{windowDesc.memory}</span>
              </div>
              <p className="text-sm text-green-800 mb-1">{windowDesc.description}</p>
              <p className="text-xs text-green-600">{windowDesc.reactivity}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>⚡ Reactive</span>
                <span>Stable 🏛️</span>
              </div>
              <Slider
                value={[settings.window]}
                min={5}
                max={100}
                step={1}
                onValueChange={(value) => updateHDKSettings({ window: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>5</span>
                <span>20</span>
                <span>60</span>
                <span>100</span>
              </div>
            </div>
          </div>

          {/* Learning Rate */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Learning Speed (Adaptation Rate)</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How quickly the model adapts to new market conditions. Higher = faster adaptation but more unstable.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-orange-100 px-2 py-1 rounded">
                {(settings.learningRate * 1000).toFixed(1)}‰
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{learningDesc.emoji}</span>
                <span className="font-semibold text-orange-900">{learningDesc.speed}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{learningDesc.stability}</span>
              </div>
              <p className="text-sm text-orange-800 mb-1">{learningDesc.description}</p>
              <p className="text-xs text-orange-600">{learningDesc.risk}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>🐌 Conservative</span>
                <span>Aggressive ⚡</span>
              </div>
              <Slider
                value={[settings.learningRate]}
                min={0.0001}
                max={0.1}
                step={0.0001}
                onValueChange={(value) => updateHDKSettings({ learningRate: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0.1‰</span>
                <span>1‰</span>
                <span>10‰</span>
                <span>100‰</span>
              </div>
            </div>
          </div>

          {/* Kernel-specific parameters */}
          {settings.kernelType === 'rbf' && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">RBF Sensitivity (Gamma)</Label>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-xs">Controls how tightly the RBF kernel fits to training data. Lower = broader patterns, Higher = more specific patterns.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <span className="text-sm font-mono bg-blue-100 px-2 py-1 rounded">
                  γ = {settings.gamma.toFixed(3)}
                </span>
              </div>
              
              <div className="relative">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>🌐 Broad patterns</span>
                  <span>Specific patterns 🎯</span>
                </div>
                <Slider
                  value={[settings.gamma]}
                  min={0.001}
                  max={1}
                  step={0.001}
                  onValueChange={(value) => updateHDKSettings({ gamma: value[0] })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>0.001</span>
                  <span>0.1</span>
                  <span>0.5</span>
                  <span>1.0</span>
                </div>
              </div>
            </div>
          )}

          {settings.kernelType === 'poly' && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">Polynomial Complexity (Degree)</Label>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-xs">The degree of polynomial curves to fit. Higher degree = more complex curves but risk of overfitting.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <span className="text-sm font-mono bg-purple-100 px-2 py-1 rounded">
                  x^{settings.polyDegree}
                </span>
              </div>
              
              <div className="relative">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>📏 Simple curves</span>
                  <span>Complex curves 🌊</span>
                </div>
                <Slider
                  value={[settings.polyDegree]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(value) => updateHDKSettings({ polyDegree: value[0] })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Linear</span>
                  <span>Quadratic</span>
                  <span>Cubic</span>
                  <span>x^10</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs">Polynomial Gamma</Label>
                  <span className="text-xs font-mono">{settings.gamma.toFixed(3)}</span>
                </div>
                <Slider
                  value={[settings.gamma]}
                  min={0.001}
                  max={1}
                  step={0.001}
                  onValueChange={(value) => updateHDKSettings({ gamma: value[0] })}
                />
              </div>
            </div>
          )}

          {settings.kernelType === 'sigmoid' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs">Sigmoid Gamma</Label>
                    <span className="text-xs font-mono">{settings.gamma.toFixed(3)}</span>
                  </div>
                  <Slider
                    value={[settings.gamma]}
                    min={0.001}
                    max={1}
                    step={0.001}
                    onValueChange={(value) => updateHDKSettings({ gamma: value[0] })}
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs">Sigmoid Offset</Label>
                    <span className="text-xs font-mono">{settings.coef0.toFixed(2)}</span>
                  </div>
                  <Slider
                    value={[settings.coef0]}
                    min={-5}
                    max={5}
                    step={0.1}
                    onValueChange={(value) => updateHDKSettings({ coef0: value[0] })}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Regularization */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Overfitting Protection (Regularization)</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">Prevents the model from memorizing training data too closely. Higher values = more protection but potentially lower performance.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="text-sm font-mono bg-red-100 px-2 py-1 rounded">
                λ = {(settings.regularization * 1000).toFixed(1)}‰
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{regularizationDesc.emoji}</span>
                <span className="font-semibold text-red-900">{regularizationDesc.level}</span>
                <span className="text-xs px-2 py-1 bg-white/60 rounded-full">{regularizationDesc.protection}</span>
              </div>
              <p className="text-sm text-red-800 mb-1">{regularizationDesc.description}</p>
              <p className="text-xs text-red-600">{regularizationDesc.performance}</p>
            </div>
            
            <div className="relative">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>🎲 Minimal safety</span>
                <span>Maximum safety 🔒</span>
              </div>
              <Slider
                value={[settings.regularization]}
                min={0.0001}
                max={0.01}
                step={0.0001}
                onValueChange={(value) => updateHDKSettings({ regularization: value[0] })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0.1‰</span>
                <span>1‰</span>
                <span>5‰</span>
                <span>10‰</span>
              </div>
            </div>
          </div>

          {/* Data Preprocessing */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Data Preprocessing</Label>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label className="text-xs">Feature Normalization Method</Label>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">How to scale different types of market data (price, volume, indicators) to work together effectively.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              
              <Select 
                value={settings.featureNormalization || 'z-score'} 
                onValueChange={(v) => updateHDKSettings({ featureNormalization: v as 'min-max' | 'z-score' | 'none' })}
              >
                <SelectTrigger className="text-sm h-9">
                  <SelectValue placeholder="Select normalization method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="min-max">📐 Min-Max Scaling - Equal weight</SelectItem>
                  <SelectItem value="z-score">📊 Z-Score - Preserve distribution</SelectItem>
                  <SelectItem value="none">🔢 No Normalization - Raw values</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">{normalizationDesc.emoji}</span>
                  <span className="font-semibold text-gray-900">{normalizationDesc.name}</span>
                </div>
                <p className="text-sm text-gray-800 mb-1">{normalizationDesc.description}</p>
                <p className="text-xs text-gray-600 mb-1">{normalizationDesc.analogy}</p>
                <p className="text-xs text-gray-600 font-medium">{normalizationDesc.effect}</p>
              </div>
            </div>

            {settings.featureNormalization === 'z-score' && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Outlier Detection Threshold</Label>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3 w-3 text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-xs">Data points beyond this many standard deviations are considered outliers and may be treated specially.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <span className="text-xs font-mono">{settings.zscoreThreshold.toFixed(1)}σ</span>
                </div>
                <Slider
                  value={[settings.zscoreThreshold]}
                  min={0.5}
                  max={3.0}
                  step={0.1}
                  onValueChange={(value) => updateHDKSettings({ zscoreThreshold: value[0] })}
                />
                <div className="flex justify-between text-xs text-gray-400">
                  <span>Sensitive (0.5σ)</span>
                  <span>Standard (2σ)</span>
                  <span>Tolerant (3σ)</span>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Switch
                checked={settings.useAdaptiveKernel}
                onCheckedChange={(checked) => updateHDKSettings({ useAdaptiveKernel: checked })}
              />
              <Label className="text-xs">Enable Adaptive Kernel (automatically adjusts kernel parameters based on market conditions)</Label>
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  };

  const renderModelParameters = () => {
    switch (model.type) {
      case 'stewie_quant':
        return renderStewieQuantParameters();
      case 'lstm':
        return renderLSTMParameters();
      case 'arima':
        return renderARIMAParameters();
      case 'xgboost':
        return renderXGBoostParameters();
      case 'stewiegpt':
        return renderStewieGPTParameters();
      case 'monte_carlo':
        return renderMonteCarloParameters();
      case 'dijkstra':
        return renderDijkstraParameters();
      case 'rl_dqn':
        return renderRLDQNParameters();
      case 'hdk':
        return renderHDKParameters();
      default:
        return <p className="text-xs text-gray-500">(No parameters for this model type)</p>;
    }
  };

  return (
    <div className="p-1 bg-white rounded-md shadow-sm ring-1 ring-inset ring-gray-200">
      {renderModelParameters()}
    </div>
  );
};